import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { lockInChatterScore, unlockChatterScore } from '../../services/firebase/chatters';
import { CHATTERS_ID } from '../../constants/game';
import { logError } from '../../utils/logger';

// Scoring phase UI shown to chatters.
// They submit their own score + predictions for regular players.
// Results are stored in chatterVotes/{uid} and aggregated by the host.
export default function ChatterScoringPhase({ game, round, chatterVotes, myVote }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [myScore, setMyScore] = useState('');
  const [predictions, setPredictions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  // Chatters predict only the regular (non-chatters) players
  const regularPlayers = game.playerIds.filter((id) => id !== CHATTERS_ID);
  const lockedChatters = Object.values(chatterVotes || {}).filter((v) => v.lockedIn).length;
  const totalChatters = Object.keys(chatterVotes || {}).length;
  const isLockedIn = myVote?.lockedIn === true;

  // Restore form values and reset submitting state when a chatter unlocks
  useEffect(() => {
    if (myVote && !myVote.lockedIn) {
      if (myVote.actualScore != null) setMyScore(String(myVote.actualScore));
      if (myVote.predictions) {
        setPredictions(
          Object.fromEntries(Object.entries(myVote.predictions).map(([k, v]) => [k, String(v)]))
        );
      }
      setSubmitting(false);
    }
  }, [myVote?.lockedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUnlock() {
    setUnlocking(true);
    try {
      await unlockChatterScore(game.id, round.id, user.uid);
    } catch (err) {
      logError(err);
    } finally {
      setUnlocking(false);
    }
  }

  function handlePrediction(uid, val) {
    setPredictions((prev) => ({ ...prev, [uid]: val }));
  }

  async function handleLockIn() {
    const score = parseFloat(myScore);
    if (isNaN(score) || score < 0 || score > 10) return;

    for (const uid of regularPlayers) {
      const p = parseFloat(predictions[uid]);
      if (isNaN(p) || p < 0 || p > 10) return;
    }

    setSubmitting(true);
    try {
      const parsedPredictions = {};
      for (const uid of regularPlayers) {
        parsedPredictions[uid] = parseFloat(predictions[uid]);
      }
      await lockInChatterScore(game.id, round.id, user.uid, {
        actualScore: score,
        predictions: parsedPredictions,
      });
    } catch (err) {
      logError(err);
      setSubmitting(false);
    }
  }

  const allFilled =
    parseFloat(myScore) >= 0 &&
    parseFloat(myScore) <= 10 &&
    regularPlayers.every((uid) => parseFloat(predictions[uid]) >= 0 && parseFloat(predictions[uid]) <= 10);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="w-full max-w-lg mx-auto space-y-6">

        {isLockedIn ? (
          /* Locked-in view */
          <div role="status" aria-live="polite" className="t-bg-surface t-border border rounded-2xl p-8 space-y-3 text-center">
            <div className="text-4xl" aria-hidden="true">✅</div>
            <h2 className="text-xl font-bold t-text">{t('chatterScoring.voteIsIn')}</h2>
            <p className="t-text-muted text-sm">
              {t('chatterScoring.chattersSubmitted', { locked: lockedChatters, total: totalChatters })}
            </p>
            <p className="text-xs t-text-disabled mt-2">
              {t('chatterScoring.waitingForReveal')}
            </p>
            <button
              type="button"
              onClick={handleUnlock}
              disabled={unlocking}
              className="text-xs t-text-muted underline hover:opacity-70 disabled:opacity-40 t-focus-ring rounded"
            >
              {unlocking ? t('scoring.unlocking') : t('scoring.changeScores')}
            </button>
          </div>
        ) : (
          /* Scoring form */
          <div className="space-y-6">
            <ScoreBlock
              label={t('chatterScoring.yourScore')}
              sublabel={t('chatterScoring.howDoYouRate')}
              value={myScore}
              onChange={setMyScore}
            />

            {regularPlayers.length > 0 && (
              <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-semibold t-text-secondary">{t('chatterScoring.yourPredictions')}</h3>
                <p className="text-xs t-text-disabled -mt-2">
                  {t('chatterScoring.whatDoYouThink')}
                </p>
                {regularPlayers.map((uid) => (
                  <ScoreBlock
                    key={uid}
                    label={game.playerNames?.[uid] || uid}
                    value={predictions[uid] || ''}
                    onChange={(v) => handlePrediction(uid, v)}
                    compact
                  />
                ))}
              </div>
            )}

            <button
              onClick={handleLockIn}
              disabled={submitting || !allFilled}
              className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
            >
              {submitting ? t('chatterScoring.submitting') : t('chatterScoring.submit')}
            </button>

            <p className="text-xs t-text-disabled text-center">
              {t('chatterScoring.voteAveraged')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const SCORE_HINT_ID = 'chatter-score-input-hint';

function ScoreBlock({ label, sublabel, value, onChange, compact = false }) {
  const { t } = useTranslation();
  return (
    <div className={compact ? '' : 't-bg-surface t-border border rounded-2xl p-5'}>
      <span id={SCORE_HINT_ID} className="sr-only">{t('chatterScoring.scoreHint')}</span>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className={`font-medium t-text ${compact ? 'text-sm' : 'text-base'}`}>{label}</p>
          {sublabel && <p className="text-xs t-text-disabled mt-0.5">{sublabel}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <input
            type="number"
            min="0"
            max="10"
            step="0.1"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="-"
            aria-label={t('chatterScoring.scoreFor', { label })}
            aria-describedby={SCORE_HINT_ID}
            className="w-16 t-bg-input t-border-input border rounded-lg px-2 py-2 t-text text-center text-lg font-bold t-focus-input"
          />
          <span className="t-text-disabled text-sm">/10</span>
        </div>
      </div>
      <div className="flex gap-1 mt-3 flex-wrap">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(String(n))}
            aria-label={`${n} out of 10`}
            className={`flex-1 min-w-8 py-1.5 rounded text-xs font-medium transition-colors t-focus-ring ${
              String(value) === String(n)
                ? 't-btn-primary'
                : 't-bg-input t-text-muted hover:opacity-80'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {/* Decimal slider */}
      <input
        type="range"
        min="0"
        max="10"
        step="0.1"
        value={value !== '' ? value : 0}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t('scoring.scoreSlider')}
        className="w-full mt-3 accent-(--c-action-bg)"
      />
    </div>
  );
}
